import { useState, useEffect, useRef } from 'react';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { Results } from '../components/Results';
import { ResultDisplay } from '../components/SmsChecker/ResultDisplay';
import { DevModeInjector } from '../components/SmsChecker/DevModeInjector';
import type { CheckUrlResponse, SmsAnalyzeResponse } from '../services/api';
import { analyzeSms } from '../services/api';
import { formatDate } from '../utils/date';
import { getBankLogo } from '../data/logos';
import { API_BASE } from '../config';

interface OfficialEntity {
  id: number;
  code: string;
  name: string;
  category: string;
  country: string;
  officialUrls: string[];
  lastVerifiedAt?: string;
}

interface ReportedScam {
  id: number;
  urlOrDomain: string;
  firstSeenAt: string;
  scamType?: string;
  description?: string;
}

interface DevTestcase {
  urlToBeTested: string;
  typeOfScam: {
    cs: string;
    en: string;
  };
  explanation: {
    cs: string;
    en: string;
  };
  expectedResult: {
    aiMetrics?: {
      brandImpersonation?: {
        closestTrustedDomain?: string;
      };
    };
  };
}

type Mode = 'url' | 'sms';

export default function Home() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('url');
  const [urlInput, setUrlInput] = useState('');
  const [smsInput, setSmsInput] = useState('');
  const [urlResult, setUrlResult] = useState<CheckUrlResponse | null>(null);
  const [smsResult, setSmsResult] = useState<SmsAnalyzeResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedTab, setSelectedTab] = useState<'banks' | 'delivery' | 'ecommerce'>('banks');
  const [topEntities, setTopEntities] = useState<OfficialEntity[]>([]);
  const [latestScams, setLatestScams] = useState<ReportedScam[]>([]);
  
  // DevMode state
  const [devModeEnabled, setDevModeEnabled] = useState(false);
  const [skipDnsCheck, setSkipDnsCheck] = useState(false);
  const [testcases, setTestcases] = useState<DevTestcase[]>([]);
  const [dnsCheckSkipped, setDnsCheckSkipped] = useState(false);
  const [selectedTestcase, setSelectedTestcase] = useState<DevTestcase | null>(null);
  
  const urlInputRef = useRef<HTMLInputElement>(null);
  const smsInputRef = useRef<HTMLTextAreaElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  // Fetch top entities for selected tab
  useEffect(() => {
    const fetchTopEntities = async () => {
      try {
        const category = selectedTab === 'banks' ? 'Bank' : selectedTab === 'delivery' ? 'Delivery' : 'E-commerce';
        const response = await fetch(`${API_BASE}/v1/entities?category=${category}&country=CZ`);
        if (response.ok) {
          const data = await response.json();
          setTopEntities(Array.isArray(data) ? data.slice(0, 7) : (data.entities || []).slice(0, 7));
        }
      } catch (err) {
        console.error('Failed to fetch entities:', err);
      }
    };
    fetchTopEntities();
  }, [selectedTab]);

  // Fetch latest scams (temporarily disabled)
  useEffect(() => {
    console.log('Fetching latest scams from /v1/scams?limit=3 is currently disabled');
    // Intentionally not calling the API at the moment
    setLatestScams([]);
  }, []);

  // Load DevMode testcases when DevMode is enabled
  useEffect(() => {
    if (devModeEnabled && testcases.length === 0) {
      fetch('/dev/scam-url-candidates.json')
        .then((res) => res.json())
        .then((data) => setTestcases(Array.isArray(data) ? data : []))
        .catch((err) => console.error('Failed to load testcases:', err));
    }
  }, [devModeEnabled, testcases.length]);

  // Clear results when switching between modes
  useEffect(() => {
    setUrlResult(null);
    setSmsResult(null);
    setError(null);
  }, [mode]);

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!urlInput.trim()) return;

    setLoading(true);
    setError(null);
    setUrlResult(null);
    setSmsResult(null);
    setDnsCheckSkipped(skipDnsCheck);

    try {
      const response = await fetch(`${API_BASE}/v1/check-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          url: urlInput.trim(),
          skipDnsCheck: skipDnsCheck || undefined, // only send if true
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || t('common.error'));
      }

      const result = await response.json();
      setUrlResult(result);
      
      // Auto-scroll to results
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSmsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!smsInput.trim()) return;

    setLoading(true);
    setError(null);
    setUrlResult(null);
    setSmsResult(null);

    try {
      const result = await analyzeSms({ content: smsInput.trim() });
      setSmsResult(result);
      
      // Auto-scroll to results
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const clearResults = () => {
    setUrlResult(null);
    setSmsResult(null);
    setError(null);
    
    // Return focus to active input
    if (mode === 'url') {
      urlInputRef.current?.focus();
    } else {
      smsInputRef.current?.focus();
    }
  };

  const handleReport = () => {
    const context = mode === 'url' ? urlInput : smsInput;
    navigate('/feedback', { state: { context, type: mode } });
  };

  const hasResults = urlResult || smsResult || error;

  // Add noindex meta when results are visible
  useEffect(() => {
    if (hasResults) {
      const metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      metaRobots.content = 'noindex, nofollow';
      metaRobots.id = 'results-noindex';
      document.head.appendChild(metaRobots);
      
      return () => {
        const existing = document.getElementById('results-noindex');
        if (existing) document.head.removeChild(existing);
      };
    }
  }, [hasResults]);

  return (
    <div className="page-container">
      <header className="header-bar">
        <div className="container py-4 flex items-center justify-between">
          <h1 className="app-title">{t('app.title')}</h1>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            
            {/* Report Button - only show when results exist */}
            {hasResults && (
              <button
                onClick={handleReport}
                className="btn-secondary"
                aria-label={t('home.reportButton')}
              >
                {t('home.reportButton')}
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="container py-8">
        {/* Segmented Control */}
        <section className="mb-8" role="group" aria-label={t('home.modeSelector')}>
          <div className="segmented-control">
            <button
              onClick={() => setMode('url')}
              className={`segmented-control__button ${
                mode === 'url'
                  ? 'segmented-control__button--active'
                  : 'segmented-control__button--inactive'
              }`}
              aria-pressed={mode === 'url'}
            >
              {t('home.urlCheck')}
            </button>
            <button
              onClick={() => setMode('sms')}
              className={`segmented-control__button ${
                mode === 'sms'
                  ? 'segmented-control__button--active'
                  : 'segmented-control__button--inactive'
              }`}
              aria-pressed={mode === 'sms'}
            >
              {t('home.smsCheck')}
            </button>
          </div>
        </section>
        {/* DevMode Box (T017-T022) */}
        {mode === 'url' && devModeEnabled && (
          <section className="card mb-4 bg-yellow-50 border-2 border-yellow-300">
            <h3 className="text-sm font-semibold text-yellow-900 mb-3">
              ⚙️ {t('devMode.toggle')}
            </h3>
            
            {/* Testcase selector */}
            <div className="mb-3">
              <label htmlFor="testcase-select" className="text-sm font-medium text-gray-700 block mb-1">
                {t('devMode.testcases')}
              </label>
              <select
                id="testcase-select"
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                onChange={(e) => {
                  const selected = testcases.find(tc => tc.urlToBeTested === e.target.value);
                  if (selected) {
                    setUrlInput(selected.urlToBeTested);
                    setSelectedTestcase(selected);
                  } else {
                    setSelectedTestcase(null);
                  }
                }}
                defaultValue=""
              >
                <option value="">{t('devMode.selectTestcase')}</option>
                {testcases.map((tc, idx) => (
                  <option key={idx} value={tc.urlToBeTested}>
                    {tc.urlToBeTested}
                  </option>
                ))}
              </select>
            </div>

            {/* Testcase details */}
            {selectedTestcase && (
              <div className="mb-3 p-3 bg-white border border-yellow-200 rounded text-sm">
                <div className="mb-2">
                  <span className="font-semibold text-gray-700">{t('devMode.typeOfScam')}:</span>
                  <span className="ml-2 text-gray-900">
                    {i18n.language === 'cs' ? selectedTestcase.typeOfScam.cs : selectedTestcase.typeOfScam.en}
                  </span>
                </div>
                <div className="mb-2">
                  <span className="font-semibold text-gray-700">{t('devMode.explanation')}:</span>
                  <p className="mt-1 text-gray-900">
                    {i18n.language === 'cs' ? selectedTestcase.explanation.cs : selectedTestcase.explanation.en}
                  </p>
                </div>
                {selectedTestcase.expectedResult?.aiMetrics?.brandImpersonation?.closestTrustedDomain && (
                  <div>
                    <span className="font-semibold text-gray-700">{t('devMode.closestTrustedDomain')}:</span>
                    <span className="ml-2 text-gray-900">
                      {selectedTestcase.expectedResult.aiMetrics.brandImpersonation.closestTrustedDomain}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Skip DNS check toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="skip-dns-check"
                checked={skipDnsCheck}
                onChange={(e) => setSkipDnsCheck(e.target.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="skip-dns-check" className="text-sm text-gray-700">
                {t('devMode.skipDnsCheck')}
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1 ml-6">
              {t('devMode.skipDnsCheckHelp')}
            </p>
          </section>
        )}
        {/* URL Check Form */}
        {mode === 'url' && (
          <section className="card mb-8">
            <form onSubmit={handleUrlSubmit}>
              <label htmlFor="url-input" className="form-label">
                {t('lookup.urlLabel')}
              </label>
              <div className="flex gap-3">
                <input
                  id="url-input"
                  ref={urlInputRef}
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder={t('lookup.urlPlaceholder')}
                  className="input-field flex-1"
                  disabled={loading}
                  required
                />
                <button
                  type="submit"
                  disabled={loading || !urlInput.trim()}
                  className="btn-primary"
                >
                  {loading ? t('lookup.checking') : t('lookup.checkButton')}
                </button>
              </div>
            </form>
          </section>
        )}

        {/* SMS Check Form */}
        {mode === 'sms' && (
          <section className="card mb-8">
            <form onSubmit={handleSmsSubmit}>
              <label htmlFor="sms-input" className="form-label">
                {t('sms.textLabel')}
              </label>
              <textarea
                id="sms-input"
                ref={smsInputRef}
                value={smsInput}
                onChange={(e) => setSmsInput(e.target.value)}
                placeholder={t('sms.textPlaceholder')}
                rows={4}
                className="input-field resize-none"
                disabled={loading}
                required
              />
              <button
                type="submit"
                disabled={loading || !smsInput.trim()}
                className="btn-primary mt-3 w-full"
              >
                {loading ? t('common.loading') : t('sms.analyze')}
              </button>
            </form>
          </section>
        )}

        {/* DevModeInjector for SMS (only in dev mode) */}
        {mode === 'sms' && devModeEnabled && (
          <DevModeInjector onInjectTestCase={(content) => setSmsInput(content)} />
        )}

        {/* Results Section */}
        {hasResults && (
          <section ref={resultsRef} className="card mb-8 relative">
            <button
              onClick={clearResults}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
              aria-label={t('home.clearResults')}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {error && (
              <div className="text-red-600 mb-4" role="alert">
                {error}
              </div>
            )}

            {/* DNS Check Skipped Warning */}
            {dnsCheckSkipped && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4" role="alert">
                <p className="text-sm text-yellow-800">
                  {t('devMode.skippedValidationWarning')}
                </p>
              </div>
            )}

            {urlResult && <Results result={urlResult} />}

            {smsResult && (
              <div className="mt-6">
                <ResultDisplay result={smsResult} />
              </div>
            )}
          </section>
        )}

        {/* Official Registry Tabs */}
        <section className="card mb-8">
          <h2 className="section-heading--xlarge">{t('home.officialRegistry')}</h2>
          
          <div className="flex gap-2 mb-4 border-b border-gray-200">
            <button
              onClick={() => setSelectedTab('banks')}
              className={`tab-button ${
                selectedTab === 'banks'
                  ? 'tab-button--active'
                  : 'tab-button--inactive'
              }`}
            >
              {t('home.tabBanks')}
            </button>
            <button
              onClick={() => setSelectedTab('delivery')}
              className={`tab-button ${
                selectedTab === 'delivery'
                  ? 'tab-button--active'
                  : 'tab-button--inactive'
              }`}
            >
              {t('home.tabDelivery')}
            </button>
            <button
              onClick={() => setSelectedTab('ecommerce')}
              className={`tab-button ${
                selectedTab === 'ecommerce'
                  ? 'tab-button--active'
                  : 'tab-button--inactive'
              }`}
            >
              {t('home.tabEcommerce')}
            </button>
          </div>

          <ul className="space-y-3">
            {topEntities.map((entity) => (
              <li key={entity.id}>
                <Link
                  to={`/entity/${entity.id}`}
                  className="clickable-row flex items-center gap-3"
                  aria-label={t('directory.viewEntity', { name: entity.name })}
                >
                  <div className="shrink-0">
                    {entity.category === 'Bank' && getBankLogo(entity.code) ? (
                      <img
                        src={getBankLogo(entity.code)!.src}
                        alt={t('alt.logoOf', { name: entity.name })}
                        className="logo-thumb"
                        loading="lazy"
                        width={40}
                        height={40}
                      />
                    ) : (
                      <div className="logo-thumb logo-placeholder rounded">
                        <span className="sr-only">{t('alt.logoOf', { name: entity.name })}</span>
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900">{entity.name}</p>
                    <p className="text-secondary truncate">{entity.officialUrls[0]}</p>
                    {entity.lastVerifiedAt && (
                      <p className="text-muted">
                        {t('directory.lastVerified')}: {formatDate(entity.lastVerifiedAt)}
                      </p>
                    )}
                  </div>
                  <span className="text-gray-400" aria-hidden="true">
                    →
                  </span>
                </Link>
              </li>
            ))}
          </ul>

          <button
            onClick={() => navigate(`/directory?category=${selectedTab}`)}
            className="btn-text mt-4 text-sm font-medium"
          >
            {t('home.viewAll')} →
          </button>
        </section>

        {/* Latest Scams */}
        <section className="card">
          <h2 className="section-heading--xlarge">{t('home.latestScams')}</h2>
          
          <ul className="space-y-4">
            {latestScams.map((scam) => (
              <li key={scam.id} className="border-b pb-4 last:border-0">
                <button
                  onClick={() => navigate(`/scam/${scam.id}`)}
                  className="w-full text-left hover:bg-gray-50 rounded p-2 -m-2 transition-colors"
                >
                  <p className="font-medium text-gray-900">{scam.urlOrDomain}</p>
                  <p className="text-secondary mt-1">{scam.description}</p>
                  {scam.firstSeenAt && (
                    <p className="text-muted mt-1">
                      {t('scams.firstSeenAt')}: {formatDate(scam.firstSeenAt)}
                    </p>
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex gap-4">
            <button
              onClick={() => navigate('/scams')}
              className="btn-text text-sm font-medium"
            >
              {t('home.viewAll')} →
            </button>
            <button
              onClick={() => navigate('/impersonation-domains')}
              className="btn-text text-sm font-medium text-orange-600"
            >
              {t('impersonationDomains.title')} →
            </button>
          </div>
        </section>
      </main>

      <footer className="footer-bar mt-12">
        <div className="container py-6 flex gap-6 text-secondary items-center">
          <button onClick={() => navigate('/disclaimer')} className="hover:text-gray-900">
            {t('disclaimer.title')}
          </button>
          <button onClick={() => navigate('/privacy')} className="hover:text-gray-900">
            {t('privacy.title')}
          </button>
          
          {/* DevMode Toggle (T016) */}
          <div className="ml-auto flex items-center gap-2">
            <input
              type="checkbox"
              id="devmode-toggle"
              checked={devModeEnabled}
              onChange={(e) => setDevModeEnabled(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="devmode-toggle" className="text-sm hover:text-gray-900 cursor-pointer">
              {t('devMode.toggle')}
            </label>
          </div>
        </div>
      </footer>
    </div>
  );
}
